import { useCallback } from 'react';
import { getApps } from 'firebase/app';
import { getAnalytics } from 'firebase/analytics';
import { AnalyticEvent, AnalyticsEvents } from '@sweatpals/shared';
import { CustomEventName, logEvent } from '@firebase/analytics';
import { useFirebaseContext } from '../../contexts';

/**
 * Hook for interacting with Firebase Analytics.
 *
 * Provides functionality to log typed analytics events and set user ID for analytics tracking.
 *
 * @example
 * ```tsx
 * const { logEventTyped, setUserIdExternally } = useFirebaseAnalytics();
 *
 * logEventTyped('login_success', { method: 'email' });
 *
 * setUserIdExternally('user123');
 * ```
 *
 * @returns An object with methods to interact with Firebase Analytics
 * @note This hook can only be used within the scope of a FirebaseContext provider
 */
export const useFirebaseAnalytics = () => {
	const { setUserIdExternally, isInitializedRef } = useFirebaseContext();

	const logEventTyped = useCallback(<T extends CustomEventName<AnalyticsEvents>>(event: T, metadata: AnalyticEvent<T>) => {
		try {
			metadata = {
				platform: 'web',
				projectPart: 'landing',
				data_stream: 'landing',
				...metadata,
			}

			const analytics = getAnalytics(getApps()[0]);
			logEvent<AnalyticsEvents>(analytics, event, metadata);

			console.info('[Firebase] Event logged: ', { event, metadata });
		} catch (e) {
			console.error('[Firebase] Error while logging event', e);
		}
	}, [isInitializedRef?.current]);

	return {
		logEventTyped,
		setUserIdExternally,
	};
}
